<script setup lang="ts">
import { computed } from 'vue'
import {
  FileCheck2,
  Landmark,
  ReceiptText,
  Store,
} from 'lucide-vue-next'
import LogoMark from '@/components/shared/LogoMark.vue'
import { Badge, Progress } from '@/components/ui'

// --- Interface-design checkpoint ---
// Intent: a calm paper-and-ink header that names the product and the
// acting context, then gets out of the way. The shell is a single
// canvas surface; pages own their own work cards. No gradients, no
// sticky left rail, no shadow hierarchy.

type ContextTone = 'default' | 'success' | 'warning' | 'error' | 'accent' | string

interface ContextItem {
  label: string
  value: string
  tone?: ContextTone
}

const props = withDefaults(defineProps<{
  eyebrow: string
  title: string
  subtitle: string
  currentStep?: number
  providerName?: string
  providerColor?: string
  contextItems?: ContextItem[]
  steps?: string[]
}>(), {
  currentStep: 0,
  providerName: '',
  providerColor: 'var(--color-primary)',
  contextItems: () => [],
  steps: () => ['Invoice check', 'Package selection', 'Connection', 'Complete'],
})

const showSteps = computed(() => props.currentStep > 0 && props.steps.length > 0)
const progressValue = computed(() => {
  if (!props.steps.length || props.currentStep <= 0) return 0
  return Math.round((props.currentStep / props.steps.length) * 100)
})

function stepState(index: number): 'complete' | 'current' | 'upcoming' {
  if (props.currentStep <= 0) return 'upcoming'
  if (index + 1 < props.currentStep) return 'complete'
  if (index + 1 === props.currentStep) return 'current'
  return 'upcoming'
}

function contextIcon(label: string) {
  const lower = label.toLowerCase()
  if (lower.includes('invoice')) return ReceiptText
  if (lower.includes('store')) return Store
  if (lower.includes('provider') || lower.includes('package') || lower.includes('account')) return Landmark
  return FileCheck2
}
</script>

<template>
  <div class="setup-shell">
    <header class="setup-shell__header">
      <div class="setup-shell__header-inner">
        <div class="setup-shell__brand">
          <div class="setup-shell__brand-mark">
            <LogoMark :size="22" />
          </div>
          <div class="setup-shell__brand-copy">
            <div class="setup-shell__eyebrow">{{ eyebrow }}</div>
            <div class="setup-shell__brand-title">LYT Accounting</div>
          </div>
        </div>

        <div class="setup-shell__title-block">
          <h1 class="setup-shell__title">{{ title }}</h1>
          <p class="setup-shell__subtitle">{{ subtitle }}</p>
        </div>

        <div class="setup-shell__header-end">
          <Badge v-if="providerName" variant="muted" class="setup-shell__provider-chip">
            <span
              class="setup-shell__provider-mark"
              :style="{ background: providerColor }"
              aria-hidden="true"
            />
            {{ providerName }}
          </Badge>
        </div>
      </div>
    </header>

    <div v-if="contextItems.length || showSteps" class="setup-shell__rail">
      <div class="setup-shell__rail-inner">
        <ol v-if="showSteps" class="setup-shell__steps">
          <li
            v-for="(step, index) in steps"
            :key="step"
            class="setup-shell__step"
            :class="`is-${stepState(index)}`"
          >
            <span class="setup-shell__step-mark">{{ index + 1 }}</span>
            <span class="setup-shell__step-label">{{ step }}</span>
          </li>
        </ol>

        <div
          v-if="showSteps && contextItems.length"
          class="setup-shell__rail-divider"
          aria-hidden="true"
        />

        <div
          v-if="contextItems.length"
          class="setup-shell__context"
        >
          <div
            v-for="item in contextItems"
            :key="`${item.label}-${item.value}`"
            class="setup-shell__context-chip"
            :class="item.tone ? `tone-${item.tone}` : ''"
          >
            <component :is="contextIcon(item.label)" class="setup-shell__context-icon" />
            <span class="setup-shell__context-label">{{ item.label }}</span>
            <span class="setup-shell__context-value">{{ item.value }}</span>
          </div>
        </div>
      </div>

      <Progress
        v-if="showSteps"
        class="setup-shell__progress"
        :value="progressValue"
      />
    </div>

    <main class="setup-shell__main">
      <slot />
    </main>
  </div>
</template>

<style scoped>
/*
 * Layered surfaces (per .interface-design/system.md):
 *   level 0: paper canvas      (this shell background)
 *   level 1: shell header band (transparent, border-soft bottom)
 *   level 2: rail strip        (surface-muted, border-soft around)
 *   level 3: page work card    (controlled by each page)
 */
.setup-shell {
  min-height: 100vh;
  background: var(--color-paper);
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.25rem 2rem;
  gap: 1rem;
}

.setup-shell__header {
  width: min(960px, 100%);
  margin: 0 auto;
  border-bottom: 1px solid var(--color-border-soft);
  padding-bottom: 1rem;
}

.setup-shell__header-inner {
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr) minmax(0, auto);
  align-items: center;
  gap: 1.25rem;
}

.setup-shell__brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.setup-shell__brand-mark {
  width: 2.1rem;
  height: 2.1rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.setup-shell__brand-copy {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.setup-shell__eyebrow {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.setup-shell__brand-title {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--color-ink);
}

.setup-shell__title-block {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.setup-shell__title {
  margin: 0;
  font-size: clamp(1.3rem, 2.2vw, 1.65rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: var(--color-ink);
}

.setup-shell__subtitle {
  margin: 0;
  font-size: 0.88rem;
  color: var(--color-ink-soft);
  max-width: 64ch;
}

.setup-shell__header-end {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.setup-shell__provider-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.setup-shell__provider-mark {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
}

/* ---- Slim chip rail under the header ---- */

.setup-shell__rail {
  width: min(960px, 100%);
  margin: 0 auto;
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  border-radius: var(--radius-sm);
  padding: 0.7rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.setup-shell__rail-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.55rem;
}

.setup-shell__steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
}

.setup-shell__step {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.55rem;
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface);
  border-radius: 999px;
  color: var(--color-ink-soft);
  font-size: 0.78rem;
  font-weight: 600;
}

.setup-shell__step-mark {
  width: 1rem;
  height: 1rem;
  border-radius: 999px;
  border: 1px solid var(--color-border-strong);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 700;
  background: var(--color-surface-muted);
}

.setup-shell__step.is-current {
  color: var(--color-ink);
  border-color: var(--color-primary);
}

.setup-shell__step.is-current .setup-shell__step-mark {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: var(--color-paper);
}

.setup-shell__step.is-complete {
  color: var(--color-ink);
  border-color: rgba(16, 185, 129, 0.32);
}

.setup-shell__step.is-complete .setup-shell__step-mark {
  border-color: var(--color-success);
  background: var(--color-success);
  color: var(--color-paper);
}

.setup-shell__rail-divider {
  width: 1px;
  align-self: stretch;
  background: var(--color-border-soft);
}

.setup-shell__context {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  flex: 1 1 auto;
}

.setup-shell__context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface);
  border-radius: var(--radius-sm);
  font-size: 0.78rem;
  color: var(--color-ink-soft);
  min-width: 0;
}

.setup-shell__context-chip.tone-success {
  border-color: rgba(16, 185, 129, 0.32);
  color: var(--color-success);
}

.setup-shell__context-chip.tone-warning {
  border-color: rgba(245, 158, 11, 0.32);
  color: var(--color-warning);
}

.setup-shell__context-chip.tone-error {
  border-color: rgba(239, 68, 68, 0.32);
  color: var(--color-error);
}

.setup-shell__context-chip.tone-accent {
  border-color: rgba(79, 70, 229, 0.32);
  color: var(--color-primary);
}

.setup-shell__context-icon {
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
}

.setup-shell__context-label {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.62rem;
  font-weight: 700;
  color: inherit;
  opacity: 0.7;
}

.setup-shell__context-value {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 18rem;
}

.setup-shell__progress {
  width: 100%;
}

/* ---- Page slot ---- */

.setup-shell__main {
  width: min(960px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (max-width: 720px) {
  .setup-shell {
    padding: 0.75rem 0.75rem 1.5rem;
    gap: 0.75rem;
  }

  .setup-shell__header-inner {
    grid-template-columns: 1fr;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .setup-shell__header-end {
    justify-content: flex-start;
  }

  .setup-shell__rail {
    padding: 0.55rem 0.6rem;
  }

  .setup-shell__rail-divider {
    display: none;
  }

  .setup-shell__context-value {
    max-width: 12rem;
  }
}
</style>
